import { useState, useEffect, useCallback, useRef } from 'react';
import { locationsApi } from '@/lib/apiServices';

export function useProvinceCity({ initialCityId = null } = {}) {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [provinceId, setProvinceId] = useState(null);
  const [cityId, setCityId] = useState(null);

  const [loadingProvinces, setLoadingProvinces] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [resolvingInitial, setResolvingInitial] = useState(!!initialCityId);
  const [error, setError] = useState('');

  const initialCityIdRef = useRef(initialCityId);

  // Load provinces once
  useEffect(() => {
    let cancelled = false;
    setLoadingProvinces(true);
    locationsApi
      .provinces({ size: 50 })
      .then(({ data }) => {
        if (!cancelled) setProvinces(data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setError('بارگذاری استان‌ها ناموفق بود');
      })
      .finally(() => {
        if (!cancelled) setLoadingProvinces(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve a city id -> its province. Runs on mount, and again whenever the
  // caller passes a different city id (e.g. a pin drop pre-filling the pair),
  // so the select is controllable from the outside.
  useEffect(() => {
    const initialId = initialCityIdRef.current;
    if (!initialId) return;

    let cancelled = false;
    setResolvingInitial(true);
    locationsApi
      .get(initialId)
      .then(({ data }) => {
        if (cancelled || !data) return;
        // data.parent is the province (LocationWithParent)
        if (data.parent?.id) {
          setProvinceId(data.parent.id);
        }
        setCityId(data.id);
      })
      .catch(() => {
        if (!cancelled) setError('بارگذاری موقعیت فعلی ناموفق بود');
      })
      .finally(() => {
        if (!cancelled) setResolvingInitial(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialCityId]);

  // Load cities whenever province changes
  useEffect(() => {
    if (!provinceId) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setLoadingCities(true);
    locationsApi
      .citiesByProvince(provinceId, { size: 100 })
      .then(({ data }) => {
        if (!cancelled) setCities(data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setError('بارگذاری شهرها ناموفق بود');
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [provinceId]);

  // Selecting a province manually (via the dropdown) should clear the
  // previously selected city -- but NOT while we're still resolving an
  // incoming value, otherwise the pre-fill would wipe its own city choice.
  const selectProvince = useCallback((id) => {
    setProvinceId(id ? Number(id) : null);
    setCityId(null);
  }, []);

  const selectCity = useCallback((id) => {
    setCityId(id ? Number(id) : null);
  }, []);

  return {
    provinces,
    cities,
    provinceId,
    cityId,
    locationId: cityId,
    loadingProvinces,
    loadingCities,
    resolvingInitial,
    error,
    selectProvince,
    selectCity,
    setCityId,
  };
}